'use client';

import classNames from 'classnames';
import { CompareTogglePill } from '../../cms-blocks/Compare/CompareTogglePill';
import { ATTR_NAMES, getAttrFromProduct } from '../../types/attrNames';
import { EnumProductBlockColorTag } from '../../types/cmsEnums';
import { CmsProduct } from '../../types/cmsProduct';
import { MaterialIconName } from '../../types/materialIconName';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '../../utils/analytics';
import { findAttributeValue } from '../../utils/attributeMeasure';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { formatCurrencyAmount } from '../../utils/helpers/formatters';
import { sessionAttributionTracker } from '../../utils/sessionAttributionTracking/sessionAttributionTracker';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { tr } from '../../utils/translations/tr';
import { useCtaUrl } from '../ApplyCta';
import { GoogleRating } from '../GoogleRating';
import { getGoogleRating } from '../GoogleReview/utils';
import { CcProductCardActions } from './CcProductCardActions';
import { CcProductCardBadge } from './CcProductCardBadge';
import { CcProductCardEarnRate } from './CcProductCardEarnRate';
import { CcProductCardEstimate } from './CcProductCardEstimate';
import { CcProductCardFeeIncome } from './CcProductCardFeeIncome';
import { CcProductCardIdentity } from './CcProductCardIdentity';
import { CcProductCardRewards } from './CcProductCardRewards';
import { TRANSLATIONS } from './i18n';
import { CcProductCardConfig, CcProductCardTracking } from './types';
import {
  formatRate,
  resolveAnnualFee,
  resolveBadge,
  resolveEarnRate,
  resolveEstimateRate,
  resolveMinIncome,
} from './utils';

export type CcProductCardProps = {
  product: CmsProduct;
  /** The badge of the card's ProductMetaConfig entry; it wins over the trending rank (FR-1.2.5). */
  editorBadge?: string;
  badgeColor?: EnumProductBlockColorTag;
  /** The badge's icon in ProductMetaConfig; without one an editor badge takes `auto_awesome`. */
  badgeIcon?: MaterialIconName;
  ctaLinkOverride?: string;
  /** The market's CC Product Card Config: without it the earnings estimate does not render. */
  estimateConfig?: CcProductCardConfig;
  /** Without it the card sends no analytics events. */
  tracking?: CcProductCardTracking;
  /**
   * Opens the product's details in place, through the listing's `useProductDetailModal` (the PDP
   * modal for a `#usePDP` product, the product panel otherwise). Without it there is no Details.
   */
  onOpenDetails?: () => void;
  /** The listing's own classes, e.g. hiding the card on mobile. */
  className?: string;
};

/**
 * The HK credit card product card (BRD-01, CMS-2000): badge band, product block, benefits in two
 * tiers, rewards, the earnings estimate and the CTA row. Values come from the product's catalogue
 * attributes, read through the same doors the Sort and Filter panel ranks them by, so a card never
 * shows a figure the panel disagrees with.
 */
export const CcProductCard: React.FC<CcProductCardProps> = ({
  product,
  editorBadge,
  badgeColor,
  badgeIcon,
  ctaLinkOverride,
  estimateConfig,
  tracking,
  onOpenDetails,
  className,
}) => {
  const context = useCmsClientContext();
  const t = getClientTranslations(TRANSLATIONS, context);
  const currency = context.currencyDisplay;
  const applyUrl = useCtaUrl({ product, ctaLinkOverride });

  const badge = resolveBadge(
    product,
    { label: editorBadge, color: badgeColor, icon: badgeIcon },
    t,
  );
  const earnRate = resolveEarnRate(product);
  const estimateRate = resolveEstimateRate(product, earnRate);
  // A miles card's estimate is not a rebate; the title is also what the accordion's events send.
  const estimateTitle =
    estimateRate?.kind === 'miles'
      ? t.cc_card_estimate_title_miles
      : t.cc_card_estimate_title;
  const minSpend = findAttributeValue(
    product.attributes,
    ATTR_NAMES.MONTHLY_SPEND_REQUIRED,
  );
  const minSpendText =
    typeof minSpend === 'number' && minSpend > 0
      ? tr('cc_card_min_monthly_spend', t, {
          amount: formatCurrencyAmount(minSpend, currency),
        })
      : undefined;
  const registrationRequired =
    getAttrFromProduct(ATTR_NAMES.REGISTRATION_REQUIRED, product)?.value ===
    true;
  const annualFee = resolveAnnualFee(product, t, currency);
  const minIncome = resolveMinIncome(product, t, currency);
  const googleRating = getGoogleRating(product);

  // The same four events in every list, told apart by the list's title (CMS-1902).
  const track = (
    event: 'product_interaction' | 'product_click_apply',
    componentType: ComponentTypeEnum,
    title: string,
    action: UserActionEnum,
  ) => {
    if (!tracking) return;
    sendProductAnalytics({
      event,
      action,
      componentType,
      componentTitle: `${tracking.listTitle}/${title}`,
      product,
      index: String(tracking.index),
      url: tracking.url,
      pushDataLayer: tracking.pushDataLayer,
      extraData: {
        component_index: tracking.componentIndex,
        ...(tracking.filterApplied !== undefined
          ? { filter_applied: tracking.filterApplied }
          : {}),
      },
    });
  };
  // The accordion's title is the one shown, so it follows the page's language.
  const trackAccordion = (title: string) => (expanded: boolean) =>
    track(
      'product_interaction',
      ComponentTypeEnum.ACCORDION,
      `Product Card_${title}`,
      expanded ? UserActionEnum.EXPAND : UserActionEnum.COLLAPSE,
    );

  return (
    // Six zones on six rows of the listing's grid (subgrid), so each zone lines up across a row of
    // cards and the CTA rows align (FR-1.1.4); outside a grid the zones simply stack.
    <article
      data-component="cc-product-card"
      data-product-id={product.uid ?? product.entryUID}
      // One shrinkable column: a truncated line must not widen the card past its grid column. Its
      // own row gap is 0, so the listing's gap only separates one row of cards from the next.
      className={classNames(
        'row-span-6 grid min-w-0 grid-cols-1 grid-rows-subgrid gap-y-0 overflow-hidden rounded-radius/card border border-borders/default bg-fill/card',
        className,
      )}
    >
      {badge && <CcProductCardBadge {...badge} />}
      {/* Without a band the card starts at the image and reserves no space for one (FR-1.2.6). Its
          block then spans the band's row too, so it sits centred in that height, not at the top. */}
      <div
        className={classNames(
          'px-spacing/padding/x-sm pb-spacing/padding/soft-12px pt-spacing/padding/x-sm',
          { 'row-span-2 flex flex-col justify-center': !badge },
        )}
      >
        <CcProductCardIdentity
          imageSrc={product.product_image}
          productName={product.product_name}
          providerName={product.provider?.provider_name}
          shortDescription={product.editorial?.ideal_for?.trim() || undefined}
          rating={
            googleRating && (
              // Opens the page's Google reviews drawer, as on ProductCard.
              <GoogleRating
                rateScore={googleRating.rateScore}
                rateCount={googleRating.rateCount}
                productId={product.uid}
              />
            )
          }
          // Renders only on a results page that compares credit cards, in a listing that has not
          // switched comparison off (CMS-2001).
          aside={<CompareTogglePill product={product} className="shrink-0" />}
        />
      </div>
      <div>
        {/* Edge to edge, as the prototype draws it: the rate on a grey band, fee and income in two
            white cells beneath it. */}
        {(earnRate || annualFee || minIncome) && (
          <div className="flex flex-col divide-y divide-borders/default border-y border-borders/default">
            {earnRate && (
              <CcProductCardEarnRate
                caption={t.cc_card_earn_rate}
                rate={
                  earnRate.kind === 'cashback'
                    ? tr('cc_card_up_to', t, {
                        rate: `${formatRate(earnRate.percent)}%`,
                      })
                    : tr('cc_card_as_low_as', t, {
                        rate: tr('cc_card_per_mile', t, {
                          amount: `${currency}${formatRate(earnRate.dollarsPerMile)}`,
                        }),
                      })
                }
                currency={
                  earnRate.kind === 'cashback'
                    ? t.cc_card_currency_cashback
                    : t.cc_card_currency_miles
                }
                explainer={
                  earnRate.kind === 'miles'
                    ? {
                        label: t.cc_card_currency_explainer_label,
                        text: t.cc_card_currency_explainer,
                      }
                    : undefined
                }
                registration={
                  registrationRequired
                    ? {
                        label: t.cc_card_registration_required,
                        note: t.cc_card_registration_note,
                      }
                    : undefined
                }
                minSpend={minSpendText}
              />
            )}
            {(annualFee || minIncome) && (
              <CcProductCardFeeIncome
                annualFee={annualFee}
                minIncome={minIncome}
              />
            )}
          </div>
        )}
      </div>
      <div className="px-spacing/padding/x-sm pt-spacing/padding/soft-12px">
        <CcProductCardRewards
          product={product}
          t={t}
          currency={currency}
          lang={context.lang}
          onToggle={trackAccordion(t.cc_card_moneyhero_exclusive)}
        />
      </div>
      <div className="px-spacing/padding/x-sm pt-spacing/padding/soft-12px">
        <CcProductCardEstimate
          rate={estimateRate}
          config={estimateConfig}
          title={estimateTitle}
          minSpend={minSpendText}
          t={t}
          currency={currency}
          onToggle={trackAccordion(estimateTitle)}
        />
      </div>
      <div className="self-end px-spacing/padding/x-sm pb-spacing/padding/x-sm pt-spacing/padding/soft-12px">
        <CcProductCardActions
          product={product}
          ctaLinkOverride={ctaLinkOverride}
          canApply={!!applyUrl}
          labels={{
            details: t.cc_card_details,
            apply: t.cc_card_apply_now,
            notAvailable: t.cc_card_not_available,
          }}
          onDetailsClick={
            onOpenDetails &&
            (() => {
              track(
                'product_interaction',
                ComponentTypeEnum.PRODUCT_CARD,
                'Button_Details',
                UserActionEnum.CLICK,
              );
              onOpenDetails();
            })
          }
          onApplyClick={() => {
            sessionAttributionTracker.recordApplyClick();
            track(
              'product_click_apply',
              ComponentTypeEnum.PRODUCT_CARD,
              'CTA_Apply Now',
              UserActionEnum.CLICK,
            );
          }}
        />
      </div>
    </article>
  );
};
